import { useEffect } from "react";

import { useDocumentContent } from "./api/useDocumentContent";
import { OPENER_LOCK_KEY } from "./coord/openerLock";
import { getTabId } from "./coord/tabId";
import { useDocumentViewerSubscriber } from "./coord/useDocumentViewerSubscriber";
import DocumentViewerPage from "./DocumentViewerPage";
import type { ViewerDocument } from "./viewer.types";

function DocumentViewer() {
  // Claim the opener lock under this window's own tabId (never the opener's —
  // see openViewerWindow.ts) so the unload cleanup below can actually match it.
  useEffect(() => {
    localStorage.setItem(OPENER_LOCK_KEY, JSON.stringify({ tabId: getTabId(), timestamp: Date.now() }));
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const lockData = localStorage.getItem(OPENER_LOCK_KEY);
      if (lockData) {
        try {
          const { tabId } = JSON.parse(lockData);
          if (tabId === getTabId()) {
            localStorage.removeItem(OPENER_LOCK_KEY);
          }
        } catch {
          // Malformed lock data, clear it
          localStorage.removeItem(OPENER_LOCK_KEY);
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const { payload, appContextLost, clearLocal } = useDocumentViewerSubscriber();
  const shouldClear = appContextLost;
  const isActive = payload?.sessionState === "active";

  const { content, isLoading } = useDocumentContent(
    payload?.appId ?? "",
    payload?.docId ?? "",
    isActive
  );

  const hasDocument = !shouldClear && isActive && Boolean(payload) && Boolean(content);
  const document: ViewerDocument | undefined =
    hasDocument && payload && content
      ? {
          fileName: payload.fileName,
          contentType: content.contentType,
          src: content.src,
          reviewedBy: payload.reviewedBy,
        }
      : undefined;

  useEffect(() => {
    if (hasDocument) {
      globalThis.document.title = `${payload?.fileName} - Approval#: ${payload?.appId ?? ""}`;
    }
  }, [hasDocument, payload?.fileName, payload?.appId]);

  return (
    <DocumentViewerPage
      document={document}
      appId={payload?.appId}
      docId={payload?.docId}
      error={isActive && !content && !isLoading}
      isLoading={isActive && isLoading}
      onDocumentMoved={clearLocal}
    />
  );
}

export default DocumentViewer;
